import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import Modal from 'LuxKit/Modal/Modal'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import LoyaltyTierBenefitAvailabilityMessageBanner from './LoyaltyTierBenefitAvailabilityMessageBanner'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const IconContainer = styled(Sheet)`
  display: grid;
  place-items: center;
  height: ${rem(32)};
  width: ${rem(32)};
  flex-shrink: 0;
  ${setSvgIconSize(20)}
`

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary
  offerUrl: string | undefined
  /**
   * Override the package-upgrades card visibility. When omitted, falls back to the flash
   * `offer.loyalty.hasPackageTypeUpgrades` flag. SPP/LPC tiles pass `true` (property rates are
   * package-upgrade-only, so the card is forced on regardless of the flash-only flag).
   */
  showPackageUpgrades?: boolean
  /**
   * Override the room-upgrades card visibility. When omitted, falls back to the flash
   * `offer.loyalty.hasRoomTypeUpgrades` flag. SPP/LPC tiles pass `false` (no room upgrades).
   */
  showRoomUpgrades?: boolean
}

function LoyaltyStayUpgradeBenefitModal({ offer, offerUrl, showPackageUpgrades, showRoomUpgrades }: Props) {
  const intl = useIntl()
  const displayTier = useAppSelector(getLoyaltyDisplayTier)
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const tierName = getLoyaltyTierDisplayName(displayTier)

  const modalTitle = intl.formatMessage(
    {
      id: 'AhZPnAObQL3al3ezMV8Y',
      defaultMessage: 'Upgrade your stay with {loyaltyProgramName}',
      description: 'Modal title for loyalty stay upgrade benefit in the loyalty section',
    },
    { loyaltyProgramName },
  )
  const viewOfferText = intl.formatMessage({
    id: 'vmhpQcbGi2qfS4bLuy_0',
    defaultMessage: 'View offer',
    description: 'Primary button text to view the offer on the offer listing or detail page',
  })
  const bodyText = intl.formatMessage(
    {
      id: 'uRgaHWfZkqqkIzDCrU05',
      defaultMessage:
        'As a {loyaltyProgramName} {tierName} member, you’ve unlocked free upgrades on this offer. Please note that these are only available on select dates.',
      description:
        'Body text explaining that the member has unlocked free upgrades on the offer',
    },
    { loyaltyProgramName, tierName },
  )
  const freePackageUpgradesHeading = intl.formatMessage({
    id: 'VLWRt_HBw2mDmh1i9Ky3',
    defaultMessage: 'Free package upgrades',
    description: 'Section heading for free package upgrades benefit section',
  })
  const freePackageUpgradesDescription = intl.formatMessage({
    id: 'YchPPA4y91uwKFwBQJxa',
    defaultMessage:
      'Get more from your escape with extras like return transfers, spa treatments or daily dinners.',
    description:
      'Description of free package upgrades benefit including extras',
  })
  const freeRoomUpgradesHeading = intl.formatMessage({
    id: 'dnOdMi-I5YHjNib9F1mm',
    defaultMessage: 'Free room upgrades',
    description: 'Section heading for free room upgrades benefit section',
  })
  const freeRoomUpgradesDescription = intl.formatMessage({
    id: 'QCoriid3PFUbm1W2j7rr',
    defaultMessage:
      'Stay in style with upgrades to bigger rooms, better views or premium locations, guaranteed.',
    description: 'Description of free room upgrades benefit on the room page',
  })

  return (
    <Modal
      title={modalTitle}
      primaryActionText={offerUrl ? viewOfferText : undefined}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={24}>
        <BodyText variant="large">{bodyText}</BodyText>
        <VerticalSpacer gap={16}>
          {(showPackageUpgrades ?? ('loyalty' in offer && offer.loyalty.hasPackageTypeUpgrades)) && (
            <Sheet padding={12} border="light-foreground" borderRadius="L">
              <Group direction="horizontal" verticalAlign="start" gap={12}>
                <IconContainer
                  background={`tier-${displayTier}-dark`}
                  borderRadius="round"
                >
                  <LineCrownIcon />
                </IconContainer>
                <div>
                  <BodyText variant="large" weight="bold">
                    {freePackageUpgradesHeading}
                  </BodyText>
                  <BodyText variant="large">
                    {freePackageUpgradesDescription}
                  </BodyText>
                </div>
              </Group>
            </Sheet>
          )}
          {(showRoomUpgrades ?? ('loyalty' in offer && offer.loyalty.hasRoomTypeUpgrades)) && (
            <Sheet padding={12} border="light-foreground" borderRadius="L">
              <Group direction="horizontal" verticalAlign="start" gap={12}>
                <IconContainer
                  background={`tier-${displayTier}-dark`}
                  borderRadius="round"
                >
                  <SolidBedIcon />
                </IconContainer>
                <div>
                  <BodyText variant="large" weight="bold">
                    {freeRoomUpgradesHeading}
                  </BodyText>
                  <BodyText variant="large">
                    {freeRoomUpgradesDescription}
                  </BodyText>
                </div>
              </Group>
            </Sheet>
          )}
        </VerticalSpacer>
        <LoyaltyTierBenefitAvailabilityMessageBanner benefitType="hotel_upgrade" />
      </Group>
    </Modal>
  )
}

export default LoyaltyStayUpgradeBenefitModal
